/**
 * Central definition of app areas/modules and entities.
 *
 * Single source of truth for area names, paths, and display labels.
 * When you need to reference an area or entity name anywhere in the app, use this.
 *
 * @example
 * import { areas, entities, navigationLabels } from '@/constants';
 *
 * // Navigation
 * <Button onClick={() => navigate(areas.packets.path)}>
 *   {navigationLabels.backTo('packets')}
 * </Button>
 *
 * // Entity labels in editors (define once at component level)
 * const ENTITY = entities.region;
 * <Tooltip title={ENTITY.labels.delete}>
 */

export const areas = {
  packets: {
    name: 'Packets',
    nameSingular: 'Packet',
    path: '/packets',
  },
  checklists: {
    name: 'Checklists',
    nameSingular: 'Checklist',
    path: '/checklists',
  },
  contacts: {
    name: 'Contacts',
    nameSingular: 'Contact',
    path: '/contacts',
  },
  dashboard: {
    name: 'Dashboard',
    nameSingular: 'Dashboard',
    path: '/',
  },
  analytics: {
    name: 'Analytics',
    nameSingular: 'Analytics',
    path: '/analytics',
  },
} as const;

export type AreaKey = keyof typeof areas;
export type Area = (typeof areas)[AreaKey];

/**
 * Entity definitions for sub-items within areas.
 *
 * Each editor component should reference its entity from here.
 * Labels are pre-computed so components just use entity.labels.delete, etc.
 *
 * @example
 * // At top of RegionEditor.tsx
 * const ENTITY = entities.region;
 *
 * // In JSX
 * <Tooltip title={ENTITY.labels.delete}>
 * <StyledDialog title={ENTITY.labels.delete}>
 */
function createEntity(singular: string, plural?: string) {
  const pluralName = plural || `${singular}s`;
  return {
    singular,
    plural: pluralName,
    labels: {
      delete: `Delete ${singular}`,
      edit: `Edit ${singular}`,
      create: `Create ${singular}`,
      add: `Add ${singular}`,
      remove: `Remove ${singular}`,
      noItemsFound: `No ${pluralName.toLowerCase()} found`,
      noItemsYet: `No ${pluralName.toLowerCase()} yet`,
    },
  } as const;
}

export const entities = {
  // Packet sub-entities
  component: createEntity('Component'),

  // Checklist sub-entities
  question: createEntity('Question'),
  choice: createEntity('Choice'),

  // Question sub-entities
  region: createEntity('Region'),

  // Contact sub-entities
  contactMethod: createEntity('Contact Method'),
  assignment: createEntity('Assignment'),
  submission: createEntity('Submission'),

  // Shared entities
  label: createEntity('Label'),
  filter: createEntity('Filter'),
  invitation: createEntity('Invitation'),
  document: createEntity('Document'),
} as const;

export type EntityKey = keyof typeof entities;
export type Entity = (typeof entities)[EntityKey];

/**
 * Navigation label generators.
 * Use these for consistent navigation text across the app.
 */
export const navigationLabels = {
  /** "Back to Packets", "Back to Checklists", etc. */
  backTo: (area: AreaKey) => `Back to ${areas[area].name}`,

  /** "Create Packet", "Create Checklist", etc. */
  create: (area: AreaKey) => `Create ${areas[area].nameSingular}`,

  /** "Edit Packet", "Edit Checklist", etc. */
  edit: (area: AreaKey) => `Edit ${areas[area].nameSingular}`,

  /** "View Packet", "View Checklist", etc. */
  view: (area: AreaKey) => `View ${areas[area].nameSingular}`,

  /** "Recent Packets", "Recent Checklists", etc. */
  recent: (area: AreaKey) => `Recent ${areas[area].name}`,

  /** "No Packets found", "No Checklists found", etc. */
  noItemsFound: (area: AreaKey) => `No ${areas[area].name.toLowerCase()} found`,

  /** "No Packets yet", "No Checklists yet", etc. */
  noItemsYet: (area: AreaKey) => `No ${areas[area].name.toLowerCase()} yet`,
} as const;
